import React, { useEffect, useState } from 'react';
import { AlertTriangle, Ban, CheckCircle, RefreshCw, Trash2 } from 'lucide-react';
import {
  fetchAdminStories,
  fetchStoryReports,
  blockStory,
  unblockStory,
  deleteStory,
  fetchStoryStatus,
  updateStoryStatus
} from '../services/api';

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  return date.toLocaleString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

export default function AdminStoryManager() {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reportsByStory, setReportsByStory] = useState({});
  const [openReports, setOpenReports] = useState({});
  const [storiesEnabled, setStoriesEnabled] = useState(true);
  const [toggleLoading, setToggleLoading] = useState(false);

  const loadStories = async () => {
    setLoading(true);
    try {
      const data = await fetchAdminStories();
      setStories(data || []);
    } catch (error) {
      console.error('Erreur chargement stories admin:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStoryStatus = async () => {
    try {
      const data = await fetchStoryStatus();
      setStoriesEnabled(!!data?.enabled);
    } catch (error) {
      console.error('Erreur chargement statut stories:', error);
    }
  };

  useEffect(() => {
    loadStories();
    loadStoryStatus();
  }, []);

  const toggleReports = async (storyId) => {
    setOpenReports((prev) => ({ ...prev, [storyId]: !prev[storyId] }));
    if (!reportsByStory[storyId]) {
      try {
        const reports = await fetchStoryReports(storyId);
        setReportsByStory((prev) => ({ ...prev, [storyId]: reports || [] }));
      } catch (error) {
        console.error('Erreur chargement signalements:', error);
      }
    }
  };

  const handleToggleBlock = async (story) => {
    try {
      if (story.status === 'blocked') {
        await unblockStory(story.id);
      } else {
        await blockStory(story.id);
      }
      await loadStories();
    } catch (error) {
      console.error('Erreur blocage story:', error);
    }
  };

  const handleDelete = async (story) => {
    const confirmDelete = window.confirm('Supprimer définitivement cette story ?');
    if (!confirmDelete) return;
    try {
      await deleteStory(story.id);
      await loadStories();
    } catch (error) {
      console.error('Erreur suppression story:', error);
    }
  };

  const handleSetStoriesEnabled = async (enabled) => {
    if (toggleLoading || enabled === storiesEnabled) return;
    setToggleLoading(true);
    try {
      await updateStoryStatus(enabled);
      setStoriesEnabled(enabled);
    } catch (error) {
      console.error('Erreur mise à jour statut stories:', error);
    } finally {
      setToggleLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold">Stories</h2>
          <p className="text-sm text-white/60">Gère les stories publiées et les signalements.</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-full px-4 py-2">
            <span className="text-sm text-white/70">Activer les stories</span>
            <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="radio"
                name="stories-enabled"
                checked={storiesEnabled === true}
                onChange={() => handleSetStoriesEnabled(true)}
                disabled={toggleLoading}
              />
              Oui
            </label>
            <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="radio"
                name="stories-enabled"
                checked={storiesEnabled === false}
                onChange={() => handleSetStoriesEnabled(false)}
                disabled={toggleLoading}
              />
              Non
            </label>
          </div>
          <button
            onClick={loadStories}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/15 border border-white/10 rounded-full transition-colors text-sm"
          >
            <RefreshCw size={16} />
            Rafraîchir
          </button>
        </div>
      </div>

      {loading ? (
        <div className="bg-white/5 border border-white/10 rounded-2xl p-6 text-white/60">
          Chargement...
        </div>
      ) : stories.length === 0 ? (
        <div className="bg-white/5 border border-white/10 rounded-2xl p-6 text-white/60">
          Aucune story pour le moment.
        </div>
      ) : (
        <div className="space-y-4">
          {stories.map((story) => {
            const hasReports = (story.report_count || 0) > 0;
            const isBlocked = story.status === 'blocked';
            const isExpired = story.is_expired;
            const reports = reportsByStory[story.id] || [];

            return (
              <div key={story.id} className="bg-white/5 border border-white/10 rounded-2xl p-4 sm:p-5 space-y-4">
                <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <div className="w-20 h-28 rounded-xl overflow-hidden bg-black/40 flex-shrink-0">
                      {story.thumbnail_url ? (
                        <img
                          src={story.thumbnail_url}
                          alt={`Story ${story.id}`}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <video
                          src={story.video_url}
                          className="w-full h-full object-cover"
                          muted
                          playsInline
                          preload="metadata"
                        />
                      )}
                    </div>
                    <div className="min-w-0 space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold truncate">{story.user_name || 'Utilisateur'}</span>
                        {isBlocked && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-red-500/15 text-red-200 border border-red-500/20 text-xs">
                            <Ban size={12} />
                            Bloquée
                          </span>
                        )}
                        {!isBlocked && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-500/15 text-emerald-200 border border-emerald-500/20 text-xs">
                            <CheckCircle size={12} />
                            Active
                          </span>
                        )}
                        {isExpired && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full bg-white/10 text-white/60 border border-white/10 text-xs">
                            Expirée
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-white/60">
                        Publiée le {formatDate(story.published_at)}
                      </div>
                      <div className="text-xs text-white/50">
                        Expire le {formatDate(story.expires_at)}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-wrap">
                    {hasReports && (
                      <button
                        onClick={() => toggleReports(story.id)}
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-500/15 text-red-200 border border-red-500/20 text-xs"
                        title="Voir les signalements"
                      >
                        <span className="w-2 h-2 rounded-full bg-red-500" />
                        {story.report_count} signalement{story.report_count > 1 ? 's' : ''}
                      </button>
                    )}
                    <button
                      onClick={() => handleToggleBlock(story)}
                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/15 border border-white/10 text-xs"
                    >
                      <Ban size={12} />
                      {isBlocked ? 'Débloquer' : 'Bloquer'}
                    </button>
                    <button
                      onClick={() => handleDelete(story)}
                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/15 border border-white/10 text-xs text-red-200"
                    >
                      <Trash2 size={12} />
                      Supprimer
                    </button>
                  </div>
                </div>

                {openReports[story.id] && (
                  <div className="border-t border-white/10 pt-4 space-y-2">
                    <div className="flex items-center gap-2 text-sm text-white/80">
                      <AlertTriangle size={14} className="text-red-300" />
                      Signalements
                    </div>
                    {reports.length === 0 ? (
                      <div className="text-xs text-white/50">Aucun signalement.</div>
                    ) : (
                      <div className="space-y-2">
                        {reports.map((report) => (
                          <div key={report.id} className="bg-black/30 border border-white/10 rounded-xl p-3">
                            <div className="text-sm text-white">{report.reason}</div>
                            <div className="text-xs text-white/50 mt-1">
                              Par {report.reporter_name || 'Utilisateur'} · {formatDate(report.created_at)}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
